import { useCallback, useEffect, useRef, useState } from "react";
import { clientLogger } from "~/logger.client";
import { SPREEDLY_SDK_URL } from "../../paymentSdkUrls";
import { useScript } from "../useScript";

// Applied to the input inside each secure iframe so the card-number/CVV text matches our other
// inputs (the iframe is cross-origin, so it can only be styled via the SDK's setStyles, not CSS).
const FIELD_STYLES = "font-size: 14px; color: #171717; line-height: 20px;";

export type SpreedlyAuthDetails = {
	environmentKey: string;
	certificateToken: string;
	nonce: string;
	timestamp: string;
	signature: string;
};

export type SpreedlySubmitDetails = {
	// Spreedly rejects tokenization with a blank first/last name.
	first_name: string;
	last_name: string;
	month: string;
	year: string;
};

type SpreedlyTokenGeneratedResponse = {
	tokenResponse?: {
		payment_method?: {
			token?: string;
		};
	};
};

// Raw payload for the log so the exact Spreedly field error is never lost behind the generic message.
const stringifyPayload = (payload: unknown): string => {
	try {
		return JSON.stringify(payload);
	} catch {
		return String(payload);
	}
};

// Spreedly's checkout SDK reports a tokenization failure as a bare message string (e.g. "Card has
// expired"); older SDK shapes wrap it in { errors: [{ message }] } or { message }. Pull out the text.
const spreedlyErrorMessage = (payload: unknown): string | undefined => {
	if (typeof payload === "string") {
		return payload.trim() || undefined;
	}
	const { errors, message } = (payload ?? {}) as { errors?: Array<{ message?: string }>; message?: string };
	const list = Array.isArray(payload) ? (payload as Array<{ message?: string }>) : errors;
	return list?.map((error) => error?.message?.trim()).find(Boolean) ?? message?.trim();
};

type SpreedlyField = "number" | "cvv";

type SpreedlyHostedFieldsInstance = {
	inAppElements: (config: { number: { containerId: string }; cvv: { containerId: string } }) => void;
	submit: (details: SpreedlySubmitDetails) => void;
	on: (event: string, handler: (payload: unknown) => void) => void;
	setStyles: (field: SpreedlyField, styles: string) => void;
	setFieldType: (field: SpreedlyField, type: string) => void;
};

type SpreedlyHostedFieldsConstructor = new (authDetails: {
	environment_key: string;
	certificate_token: string;
	nonce: string;
	timestamp: string;
	signature: string;
}) => SpreedlyHostedFieldsInstance;

declare global {
	interface Window {
		SpreedlyHostedFields?: SpreedlyHostedFieldsConstructor;
	}
}

type SpreedlyCallbacks = {
	onToken: (token: string) => void;
	// Spreedly's failure message, passed up so the form can show it; undefined → the form shows a generic message.
	onError: (message?: string) => void;
};

type UseSpreedlyProps = {
	authDetails?: SpreedlyAuthDetails;
	numberContainerId: string;
	cvvContainerId: string;
	callbacks: SpreedlyCallbacks;
};

export const useSpreedly = ({ authDetails, numberContainerId, cvvContainerId, callbacks }: UseSpreedlyProps) => {
	const [isReady, setIsReady] = useState(false);
	const instanceRef = useRef<SpreedlyHostedFieldsInstance | null>(null);
	const callbacksRef = useRef<SpreedlyCallbacks>(callbacks);
	callbacksRef.current = callbacks;

	// Wait until we have authDetails to render hosted fields, then poll for the SDK global.
	const scriptLoaded = useScript(SPREEDLY_SDK_URL, {
		enabled: !!authDetails,
		isReady: () => !!window.SpreedlyHostedFields,
		logGroup: "SPREEDLY_SCRIPT_LOAD_ERROR",
	});

	useEffect(() => {
		if (!scriptLoaded || !authDetails || !window.SpreedlyHostedFields) {
			return;
		}

		const instance = new window.SpreedlyHostedFields({
			environment_key: authDetails.environmentKey,
			certificate_token: authDetails.certificateToken,
			nonce: authDetails.nonce,
			timestamp: authDetails.timestamp,
			signature: authDetails.signature,
		});

		instance.on("tokenGenerated", (payload) => {
			const token = (payload as SpreedlyTokenGeneratedResponse)?.tokenResponse?.payment_method?.token;
			if (token) {
				callbacksRef.current.onToken(token);
			} else {
				clientLogger.error(
					"Spreedly tokenGenerated returned no payment method token",
					new Error("Spreedly tokenGenerated returned no payment method token"),
					{ logGroup: "SPREEDLY_TOKENIZATION_ERROR" },
				);
				callbacksRef.current.onError();
			}
		});
		// Field-level failures surface on the SDK's "error" event (bad card number, missing CVV, rejected
		// AuthDetails, …). They fire client-side and never reach the backend, so log the detail here and pass
		// the message up for the form to show instead of a generic one.
		const handleFieldError = (payload: unknown) => {
			const message = spreedlyErrorMessage(payload);
			clientLogger.error(
				`Spreedly tokenization failed${message ? `: ${message}` : ""}`,
				new Error(message ?? "Spreedly tokenization failed"),
				{
					logGroup: "SPREEDLY_TOKENIZATION_ERROR",
					detail: stringifyPayload(payload),
					spreedlyMessage: message,
				},
			);
			callbacksRef.current.onError(message);
		};
		instance.on("error", handleFieldError);

		// Style the iframe inputs once the secure fields are ready so they match our other inputs.
		instance.on("ready", () => {
			instance.setFieldType("number", "text");
			instance.setStyles("number", FIELD_STYLES);
			instance.setStyles("cvv", FIELD_STYLES);
		});

		instance.inAppElements({
			number: { containerId: numberContainerId },
			cvv: { containerId: cvvContainerId },
		});

		instanceRef.current = instance;
		setIsReady(true);

		return () => {
			instanceRef.current = null;
			setIsReady(false);
		};
	}, [scriptLoaded, authDetails, numberContainerId, cvvContainerId]);

	const submit = useCallback((details: SpreedlySubmitDetails) => {
		const instance = instanceRef.current;
		if (!instance) {
			callbacksRef.current.onError();
			return;
		}
		instance.submit(details);
	}, []);

	return { isReady, submit };
};
